import React, { useState, useEffect, useCallback } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useCompany } from "../../../contexts/CompanyContext";
import { useColorMode } from "../../../contexts/ColorModeContext";
import { getAdminChartColors } from "./chartTheme";
import { adminApi } from "../../../services";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

interface WeeklyRevenueData {
  week: string;
  weekStart: string;
  revenue: number;
  projectedRevenue: number;
}

interface WeeklyRevenueChartProps {
  professionalId?: number;
}

export const WeeklyRevenueChart: React.FC<WeeklyRevenueChartProps> = ({
  professionalId,
}) => {
  const { company } = useCompany();
  const [data, setData] = useState<WeeklyRevenueData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState(new Date());

  const goToPreviousMonth = () => {
    setSelectedDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  };

  const goToNextMonth = () => {
    setSelectedDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  };

  const fetchWeeklyRevenue = useCallback(async () => {
    if (!company?.id) return;

    setLoading(true);
    setError(null);

    try {
      // Only pass professionalId if company is not car
      const professionalIdToUse = company?.category !== "car" ? professionalId : undefined;
      const response = await adminApi.getLast4WeeksRevenue(
        selectedDate.getFullYear(),
        selectedDate.getMonth() + 1,
        professionalIdToUse
      );

      if (response.success && response.data) {
        const formattedData = response.data.map((item) => ({
          ...item,
          weekStart: new Date(item.weekStart + "T12:00:00").toLocaleDateString(
            "pt-BR",
            {
              day: "2-digit",
              month: "2-digit",
              timeZone: "America/Sao_Paulo",
            }
          ),
        }));
        setData(formattedData);
      } else {
        throw new Error(response.error || "Failed to fetch weekly revenue");
      }
    } catch (err) {
      setError("Erro ao carregar receita semanal");
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, company?.id, company?.category, professionalId]);

  useEffect(() => {
    fetchWeeklyRevenue();
  }, [fetchWeeklyRevenue]);

  const WeeklyRevenueChartContent: React.FC = () => {
    const { isDark } = useColorMode();
    const chartColors = getAdminChartColors(isDark);
    const chartConfig = {
      data: {
        labels: (data || []).map((item) => item.weekStart),
        datasets: [
          {
            label: "Receita Real (R$)",
            data: (data || []).map((item) => item.revenue),
            borderColor: "rgba(16, 185, 129, 1)",
            backgroundColor: "rgba(16, 185, 129, 0.2)",
            borderWidth: 2,
            fill: true,
            tension: 0.1,
          },
          {
            label: "Receita Projetada (R$)",
            data: (data || []).map((item) => item.projectedRevenue),
            borderColor: "rgba(59, 130, 246, 1)",
            backgroundColor: "rgba(59, 130, 246, 0.2)",
            borderWidth: 2,
            borderDash: [5, 5],
            fill: false,
            tension: 0.1,
          },
        ],
      },
      options: {
        responsive: true,
        plugins: {
          legend: {
            position: "bottom" as const,
            labels: { color: chartColors.text },
          },
          title: {
            display: true,
            text: "Receita das Últimas 4 Semanas",
            color: chartColors.title,
            padding: {
              bottom: 40,
            },
            font: {
              size: window.innerWidth < 640 ? 14 : 16,
            },
          },
          datalabels: {
            display: window.innerWidth >= 640,
            anchor: "end" as const,
            align: "top" as const,
            offset: 10,
            formatter: function (value: any) {
              return `R$ ${value.toLocaleString()}`;
            },
            color: chartColors.text,
            font: {
              weight: "bold" as const,
              size: window.innerWidth < 640 ? 9 : 11,
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: { color: chartColors.grid },
            ticks: {
              callback: function (value: any) {
                return `R$ ${value.toLocaleString()}`;
              },
              color: chartColors.muted,
              font: {
                size: window.innerWidth < 640 ? 10 : 12,
              },
            },
          },
          x: {
            grid: { color: chartColors.grid },
            ticks: {
              color: chartColors.muted,
              font: {
                size: window.innerWidth < 640 ? 10 : 12,
              },
            },
          },
        },
      },
    };

    return <Line data={chartConfig.data} options={chartConfig.options} />;
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl sm:rounded-2xl shadow-medium border border-gray-100 dark:border-slate-700 p-3 sm:p-4 md:p-6">
      {/* Month Navigation Header */}
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-800 dark:text-slate-100 truncate flex-1 mr-2">
          {selectedDate
            .toLocaleDateString("pt-BR", { month: "long", year: "numeric" })
            .replace(/^\w/, (c) => c.toUpperCase())}
        </h3>
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          <button
            onClick={goToPreviousMonth}
            disabled={loading}
            className="p-1.5 sm:p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors duration-200 text-gray-600 dark:text-slate-300 hover:text-gray-800 dark:hover:text-white disabled:opacity-50"
            title="Mês anterior"
          >
            <svg
              className="w-4 h-4 sm:w-5 sm:h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <button
            onClick={goToNextMonth}
            disabled={loading}
            className="p-1.5 sm:p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors duration-200 text-gray-600 dark:text-slate-300 hover:text-gray-800 dark:hover:text-white disabled:opacity-50"
            title="Próximo mês"
          >
            <svg
              className="w-4 h-4 sm:w-5 sm:h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 sm:py-16 md:py-20">
          <div className="w-6 h-6 sm:w-8 sm:h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4">
          <p className="text-red-600 text-xs sm:text-sm">{error}</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <WeeklyRevenueChartContent />
        </div>
      )}
    </div>
  );
};
